import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { dashboardApi } from '../../services/api';
import { Calendar, FileText, Award, TrendingUp, User } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const StudentDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardApi.getStudentStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8 text-primary" />
      </div>
    );
  }

  const attendanceData = [
    { name: 'Present', value: stats?.present_days || 0, color: '#22C55E' },
    { name: 'Absent', value: (stats?.total_attendance_days || 0) - (stats?.present_days || 0), color: '#EF4444' },
  ];

  const getGradeColor = (percentage) => {
    if (percentage >= 90) return 'text-green-500';
    if (percentage >= 75) return 'text-blue-500';
    if (percentage >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="space-y-6" data-testid="student-dashboard">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Student Dashboard</h1>
          <p className="text-muted-foreground mt-1">Track your academic progress and attendance</p>
        </div>
        {stats?.student && (
          <Card className="border border-border">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-medium">{stats.student.name}</p>
                <p className="text-sm text-muted-foreground">Roll: {stats.student.roll_number}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-border card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
              <span className={`text-2xl font-bold ${stats?.attendance_percentage >= 75 ? 'text-green-500' : 'text-red-500'}`}>
                {stats?.attendance_percentage || 0}%
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Attendance Rate</p>
            <Progress value={stats?.attendance_percentage || 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card className="border border-border card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-secondary/10">
                <FileText className="h-6 w-6 text-secondary" />
              </div>
              <span className={`text-2xl font-bold ${getGradeColor(stats?.marks_percentage || 0)}`}>
                {stats?.marks_percentage || 0}%
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Academic Score</p>
            <Progress value={stats?.marks_percentage || 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card className="border border-border card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-green-500/10">
                <Award className="h-6 w-6 text-green-500" />
              </div>
              <span className="text-2xl font-bold">{stats?.present_days || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground">Days Present</p>
            <p className="text-xs text-muted-foreground mt-1">out of {stats?.total_attendance_days || 0} days</p>
          </CardContent>
        </Card>

        <Card className="border border-border card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg bg-purple-500/10">
                <TrendingUp className="h-6 w-6 text-purple-500" />
              </div>
              <span className="text-2xl font-bold">{stats?.total_exams || 0}</span>
            </div>
            <p className="text-sm text-muted-foreground">Total Exams</p>
            <p className="text-xs text-muted-foreground mt-1">Completed assessments</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Overview */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Attendance Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={attendanceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {attendanceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-6">
                {attendanceData.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-sm text-muted-foreground">{item.name}: {item.value} days</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Info */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.student ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary">{stats.student.name?.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-lg font-medium">{stats.student.name}</p>
                    <p className="text-sm text-muted-foreground">{stats.student.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Roll Number</p>
                    <p className="font-medium">{stats.student.roll_number}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Class</p>
                    <p className="font-medium">{stats.student.class_id}</p>
                  </div>
                  {stats.student.gender && (
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Gender</p>
                      <p className="font-medium capitalize">{stats.student.gender}</p>
                    </div>
                  )}
                  {stats.student.phone && (
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="font-medium">{stats.student.phone}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">Profile not available</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StudentDashboard;
